<script setup lang="ts">
    import {
        computed,
        onBeforeUnmount,
        ref,
        useTemplateRef,
        watch,
        defineAsyncComponent,
    } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useDropZone } from '@vueuse/core'
    import { storeToRefs } from 'pinia'
    import { generateId } from 'ai'
    import PkAuroraCanvas from './PkAuroraCanvas.vue'
    import PkAvatar from './PkAvatar.vue'
    import PkChatbotMessages from './PkChatbotMessages.vue'
    import PkChatbotInput from './PkChatbotInput.vue'
    import PkChatbotEmptyState from './PkChatbotEmptyState.vue'
    import PkVoiceSession from './PkVoiceSession.vue'
    import { useVoiceSession } from './useVoiceSession'
    import { formAnswerAsSpoken } from './utils'
    import {
        useChatbotStore,
        useLocalizedString,
        useGreeting,
        VOICE_LINE_ID_PREFIX,
    } from 'composables'
    import type { UIChatMessage } from 'models'

    const PkToolShowForm = defineAsyncComponent(
        () => import('./PkToolShowForm.vue'),
    )
    const PkToolShowContactForm = defineAsyncComponent(
        () => import('./PkToolShowContactForm.vue'),
    )
    const PkToolShowSuggestedReply = defineAsyncComponent(
        () => import('./PkToolShowSuggestedReply.vue'),
    )
    const PkToolShowSources = defineAsyncComponent(
        () => import('./PkToolShowSources.vue'),
    )
    const PkToolRequestGeolocation = defineAsyncComponent(
        () => import('./PkToolRequestGeolocation.vue'),
    )
    const PkToolRequestOAuthConnection = defineAsyncComponent(
        () => import('./PkToolRequestOAuthConnection.vue'),
    )
    const PkToolShowLocation = defineAsyncComponent(
        () => import('./PkToolShowLocation.vue'),
    )
    const PkToolShowDiagram = defineAsyncComponent(
        () => import('./PkToolShowDiagram.vue'),
    )

    const props = defineProps<{
        agentId: string
        /** Mirrors the PkChatbot `modifier` prop: `fullscreen` shows a centered empty state (greeting) instead of the welcome message, `panel` behaves like `widget` */
        modifier?: 'widget' | 'fullscreen' | 'panel'
        /** Display name of the current user, used for the `{name}` greeting placeholder */
        userName?: string
    }>()

    const emit = defineEmits<{
        'show-info': [message: UIChatMessage]
        revise: [message: UIChatMessage]
    }>()

    const store = useChatbotStore(props.agentId)

    const {
        agentInterface,
        agentFileUpload,
        agentVoice,
        actions,
        revisedAnswers,
        messages,
        chat,
        messageFeedbacks,
        feedbackDialogMessage,
        isFeedbackSubmitting,
        isFeedbackSubmitted,
        feedbackSubmitError,
        isLeadSubmitted,
        isLoadingSubmitLead,
        submitLeadError,
        input,
        inputMessagePlaceholder,
        isConversationBlocked,
        baseUrl,
        pendingAttachments,
        isAttachmentLimitReached,
        fileLimitError,
        apiClient,
        isDark,
    } = storeToRefs(store)

    const {
        handleSubmit: storeHandleSubmit,
        stopGeneration,
        regenerate,
        onUpvote,
        onDownvote,
        onFeedback,
        onFeedbackSubmit,
        onLeadSubmit,
        startNewChat,
        addToolOutput,
        addToolApprovalResponse,
        handleFileSelect,
        clearFileLimitError,
        refreshMessages,
        appendMessage,
    } = store

    const { t: $t } = useI18n({ useScope: 'global' })

    const attachmentNotice = computed(() => {
        const err = fileLimitError.value
        if (!err) {
            return undefined
        }
        return err.reason === 'per-chat'
            ? $t('message.fileLimitPerChat', { max: err.max })
            : $t('message.fileLimitPerMessage', { max: err.max })
    })

    const dismissableNotice = useLocalizedString(
        () => agentInterface.value?.dismissableNotice,
    )

    // Fullscreen empty state: shown until the user sends the first message.
    // The synthetic welcome message is suppressed by the store in fullscreen
    // (`hideWelcomeMessage`), the greeting takes its place.
    const hasUserMessages = computed(() =>
        messages.value.some((message) => message.role === 'user'),
    )
    const showEmptyState = computed(
        () => props.modifier === 'fullscreen' && !hasUserMessages.value,
    )
    const greeting = useGreeting(agentInterface, () => props.userName)

    const chatViewEl = useTemplateRef<HTMLDivElement>('chatViewEl')

    const handleExpandSourceContext = async (payload: {
        documentId: string
        chunkIndex: number
    }) => {
        const result = await apiClient.value.expandSourceContext(
            props.agentId,
            payload.documentId,
            payload.chunkIndex,
        )
        return result.content
    }

    const handleDownloadSource = async (documentId: string) => {
        const result = await apiClient.value.downloadSourceDocument(
            props.agentId,
            documentId,
        )
        window.open(result.downloadUrl, '_blank')
    }

    const handleFileDrop = (files: File[] | null) => {
        if (!agentFileUpload.value?.enabled || !files) {
            return
        }
        for (const file of files) {
            handleFileSelect(file)
        }
    }

    const { isOverDropZone } = useDropZone(chatViewEl, {
        dataTypes: computed(
            () => agentFileUpload.value?.allowedMimeTypes ?? [],
        ),
        onDrop: handleFileDrop,
    })

    // #region voice
    /**
     * A spoken turn is a normal turn: the provider calls our backend, which
     * persists it into this very conversation. So the client does not answer
     * anything here, it only shows the transcript as it arrives and then reloads
     * the conversation from the server, which is the version that also carries
     * sources and tool results. When the call ends the input comes back with the
     * whole exchange above it, ready to continue in writing.
     */
    const isMuted = ref(false)
    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    let callPoller: ReturnType<typeof setInterval> | undefined
    // The session closes asynchronously, so `onTurnEnd` can still fire after
    // this view is gone: without the flag it would schedule a reload of a
    // conversation nobody is showing.
    let isUnmounted = false

    /**
     * Reloads the conversation, and comes back for the rest while the server
     * is still behind. A turn is written when it ends, the caller hears it a
     * moment earlier, and a slow turn widens that gap: one reload could land
     * before the write and leave the tool results and sources of that turn
     * invisible until the page was reloaded by hand. Attempts back off and
     * stop, so a turn that never persists costs a few requests, not a loop.
     */
    const REFRESH_RETRIES = 4
    const scheduleRefresh = (delayMs: number, attempt = 0) => {
        clearTimeout(refreshTimer)
        if (isUnmounted) {
            return
        }
        refreshTimer = setTimeout(() => {
            void refreshMessages().then((behind) => {
                if (behind && attempt < REFRESH_RETRIES) {
                    scheduleRefresh(delayMs * 2, attempt + 1)
                }
            })
        }, delayMs)
    }

    /**
     * True between the caller finishing a sentence and the answer starting.
     *
     * On a call nothing of the turn passes through this client, so the feed has
     * no way of its own to show that something is happening: the question sits
     * there alone until the answer is written and reloaded, which on a
     * retrieving turn is a long ten seconds of a screen that looks stuck.
     */
    const isAwaitingSpokenAnswer = ref(false)

    const voice = useVoiceSession({
        fetchGrant: async () => {
            const grant = await apiClient.value.createVoiceSession(
                props.agentId,
                store.localChatId,
                {
                    deviceContext: store.buildDeviceContext(),
                    externalContext: store.externalContext,
                },
            )
            // The backend decides which conversation the call writes into, and
            // it may not be the one asked for: a stale id (a deleted chat, or
            // one left over from another agent) opens a new conversation rather
            // than failing the call. Follow it, or the transcript would land
            // somewhere this view is not showing.
            if (grant.chatId !== store.localChatId) {
                store.localChatId = grant.chatId
            }
            return grant
        },
        closeGrant: (sessionId) =>
            apiClient.value.closeVoiceSession(props.agentId, sessionId),
        onTranscript: (entry) => {
            // The caller stopped talking: from here until the agent speaks, the
            // turn is ours to show as working.
            isAwaitingSpokenAnswer.value = entry.role === 'user'
            appendMessage({
                // Marked as its own id space so a reload of the persisted
                // conversation replaces these rather than duplicating them,
                // and keeps the ones it does not carry yet.
                id: `${VOICE_LINE_ID_PREFIX}${entry.role}-${generateId()}`,
                role: entry.role === 'user' ? 'user' : 'assistant',
                parts: [{ type: 'text', text: entry.text }],
            })
        },
        // The persisted answer arrives a moment after the provider finished
        // speaking it, so the reload waits for the write to land.
        onTurnEnd: () => {
            isAwaitingSpokenAnswer.value = false
            scheduleRefresh(1200)
        },
    })

    // The answer being spoken is the end of the wait, whether or not its
    // transcript has arrived yet.
    watch(
        () => voice.isSpeaking.value,
        (speaking) => {
            if (speaking) {
                isAwaitingSpokenAnswer.value = false
            }
        },
    )

    const isVoiceEnabled = computed(() => agentVoice.value?.enabled === true)

    /** What the session bar and the visualiser render. */
    const voiceState = computed(() => {
        if (voice.error.value) {
            return 'error' as const
        }
        if (voice.isConnecting.value) {
            return 'connecting' as const
        }
        if (voice.isSpeaking.value) {
            return 'speaking' as const
        }
        return voice.isActive.value ? ('listening' as const) : ('idle' as const)
    })

    /** Whoever is on air feeds the bars: the user, then the agent answering. */
    const voiceChannel = computed<'input' | 'output'>(() =>
        voice.isSpeaking.value ? 'output' : 'input',
    )
    const sampleVoiceLevel = () => voice.readLevel(voiceChannel.value)
    const sampleVoiceSpectrum = () => voice.readSpectrum(voiceChannel.value)

    /**
     * While the call is up, reload the conversation on a timer.
     *
     * The turn is written by the provider calling our backend, so this client
     * has no stream to follow and nothing local tells it when the write landed:
     * every trigger available here is an approximation of it. Reloading on the
     * end of the spoken answer alone left the tool results and the caller's own
     * question off the screen until the page was reloaded by hand (reported
     * 2026-08-06), so the timer is the floor: whatever the provider does or
     * does not report, the screen catches up within a few seconds.
     */
    const CALL_POLL_MS = 3000
    /**
     * How long the poll outlives the call. Hanging up does not stop the turn
     * server-side: it keeps running and is written when it ends, so the last
     * write can land well after the line closed. A backed-off retry chain
     * capped at four attempts gave up in about eighteen seconds and left that
     * turn (its widgets above all) off the screen until the page was reloaded
     * by hand (reported 2026-08-07).
     */
    const HANG_UP_GRACE_MS = 60_000
    /** Deadline of the after-call polling, set the moment the call is over. */
    let pollUntil: number | undefined
    const stopCallPolling = () => {
        clearInterval(callPoller)
        callPoller = undefined
        pollUntil = undefined
    }
    const startCallPolling = () => {
        stopCallPolling()
        callPoller = setInterval(() => {
            if (isUnmounted) {
                stopCallPolling()
                return
            }
            if (voice.isActive.value) {
                pollUntil = undefined
            } else {
                // The call is over, however it ended (hang-up, provider
                // disconnect, time cap): poll through the grace window, so
                // the write of the last turn still reaches the screen.
                pollUntil ??= Date.now() + HANG_UP_GRACE_MS
                if (Date.now() > pollUntil) {
                    stopCallPolling()
                    return
                }
            }
            void refreshMessages()
        }, CALL_POLL_MS)
    }

    const startVoice = async () => {
        isMuted.value = false
        await voice.start()
        if (voice.isActive.value) {
            startCallPolling()
        }
    }

    const stopVoice = async () => {
        clearTimeout(refreshTimer)
        isAwaitingSpokenAnswer.value = false
        await voice.stop()
        if (isUnmounted) {
            return
        }
        // First look right away; the poll, which deliberately survives the
        // call (see HANG_UP_GRACE_MS), covers the write that lands later.
        void refreshMessages()
    }

    /** Leaves the failure behind and gives the written input back. */
    const dismissVoiceError = () => {
        voice.dismissError()
    }

    /** Same button, one step: forget the failure and open a new call. */
    const retryVoice = async () => {
        voice.dismissError()
        await startVoice()
    }

    /**
     * Tool calls this client answered while the call was up, by id.
     *
     * On a call the answer does not go back to the tool call (it goes to the
     * agent as speech), so nothing in the persisted part ever changes to say it
     * was given: without this the same widget stays answerable forever and the
     * caller can send the same thing three times.
     */
    const readToolCallId = (part: unknown) =>
        (part as { toolCallId?: string })?.toolCallId ?? ''

    const answeredOnCall = ref(new Set<string>())
    const isAnsweredOnCall = (part: unknown) =>
        answeredOnCall.value.has(readToolCallId(part))

    /**
     * The verdicts of the confirms answered ON A CALL, by toolCallId. A spoken
     * verdict never resolves the tool part (the answer travels as a user
     * message), so without this the card kept its buttons and every extra tap
     * said "Confermo" again (three times in a row, measured 2026-08-07).
     * Recorded only when the send went through: `answerOnCall` leaves a failed
     * send unanswered on purpose, so the caller can tap again.
     */
    const confirmVerdictsOnCall = ref(new Map<string, boolean>())

    /**
     * Something the caller answered on screen during a call.
     *
     * In writing an answer resumes the suspended tool call, which is what makes
     * the agent continue. On a call there is no suspended call to resume: the
     * turn that drew the widget closed it on its own (a call cannot wait for a
     * screen), so the answer is said on the caller's behalf and comes back
     * spoken, in the same conversation.
     *
     * Returns true when it was said, and then the written path must NOT run:
     * `addToolOutput` would resume the turn in writing and the caller would get
     * two answers, one of them silent.
     */
    const answerOnCall = (part: unknown, spoken: string): boolean => {
        if (!voice.isActive.value) {
            return false
        }
        if (voice.sendUserMessage(spoken)) {
            answeredOnCall.value = new Set(answeredOnCall.value).add(
                readToolCallId(part),
            )
            scheduleRefresh(1200)
            return true
        }
        // The call is up and the answer did not get through. Saying it in
        // writing here would answer a question nobody asked in writing, so the
        // widget stays as it is and the caller can try again.
        return true
    }

    /** A form or a set of options, answered on screen. */
    const onFormAnswer = (
        tool: 'showForm' | 'showMultipleChoice',
        part: unknown,
        answer: string | { answers: { id: string; value: unknown }[] },
    ) => {
        if (answerOnCall(part, formAnswerAsSpoken(part, answer))) {
            return
        }
        addToolOutput({
            tool,
            toolCallId: readToolCallId(part),
            output: answer,
        })
    }

    /** A confirmation card, answered on screen. */
    const onConfirmRespond = (payload: {
        toolCallId: string
        confirmed: boolean
    }) => {
        const spoken = payload.confirmed
            ? $t('message.voiceConfirmed')
            : $t('message.voiceCancelled')
        if (answerOnCall({ toolCallId: payload.toolCallId }, spoken)) {
            if (answeredOnCall.value.has(payload.toolCallId)) {
                confirmVerdictsOnCall.value = new Map(
                    confirmVerdictsOnCall.value,
                ).set(payload.toolCallId, payload.confirmed)
            }
            return
        }
        addToolOutput({
            tool: 'requestConfirm',
            toolCallId: payload.toolCallId,
            output: payload.confirmed ? 'confirmed' : 'cancelled',
        })
    }

    /** A suggested reply: tapping it is saying it. */
    const onSuggestedReply = (part: unknown, reply: string) => {
        if (answerOnCall(part, reply)) {
            return
        }
        input.value = reply
        storeHandleSubmit()
    }

    /** The position the caller shared from the browser. */
    const onGeolocation = (
        part: unknown,
        result: {
            latitude?: number
            longitude?: number
            displayName?: string
            error?: string
        },
    ) => {
        const spoken = result.error
            ? $t('message.voiceLocationRefused')
            : $t('message.voiceLocationShared', {
                  place:
                      result.displayName ??
                      `${result.latitude}, ${result.longitude}`,
              })
        if (answerOnCall(part, spoken)) {
            return
        }
        addToolOutput({
            tool: 'requestGeolocation',
            toolCallId: readToolCallId(part),
            output: result,
        })
    }

    /** An integration the caller connected from the card on screen. */
    const onOAuthConnected = (part: unknown, mcpServerId: string) => {
        if (answerOnCall(part, $t('message.voiceConnectionDone'))) {
            return
        }
        addToolOutput({
            tool: 'requestOAuthConnection',
            toolCallId: readToolCallId(part),
            output: { connected: true, mcpServerId },
        })
    }

    const toggleVoiceMute = () => {
        isMuted.value = !isMuted.value
        voice.setMuted(isMuted.value)
    }

    onBeforeUnmount(() => {
        isUnmounted = true
        clearTimeout(refreshTimer)
        stopCallPolling()
    })
    // #endregion voice
</script>

<template>
    <div
        ref="chatViewEl"
        class="pk-chatbot-view-chat"
        :class="{
            'pk-chatbot-view-chat--dragover':
                isOverDropZone && agentFileUpload?.enabled,
            'pk-chatbot-view-chat--empty': showEmptyState,
        }">
        <!-- #region empty state (fullscreen) / messages — crossfade out-in -->
        <Transition name="pk-chatbot-view-chat-fade" mode="out-in">
            <PkChatbotEmptyState
                v-if="showEmptyState"
                :greeting="greeting"
                :logo="agentInterface?.logo"
                :name="store.name" />
            <PkChatbotMessages
                v-else
                class="flex flex-col flex-1 min-h-0 p-md overflow-y-auto"
                interactive
                :messages="messages"
                :confirm-verdicts-on-call="confirmVerdictsOnCall"
                :status="chat.status"
                :error="chat.error"
                :main-color="agentInterface?.mainColor"
                :text-color="agentInterface?.textColor"
                :revised-answers="revisedAnswers"
                :actions="actions"
                :message-feedbacks="messageFeedbacks"
                :feedback-message-id="feedbackDialogMessage?.id"
                :feedback-loading="isFeedbackSubmitting"
                :feedback-submitted="isFeedbackSubmitted"
                :feedback-error="feedbackSubmitError"
                :show-extended-steps="agentInterface?.showExtendedSteps"
                :awaiting-spoken-answer="isAwaitingSpokenAnswer"
                :is-dark="isDark"
                :show-scroll-to-bottom="modifier === 'fullscreen'"
                @feedback-submit="onFeedbackSubmit($event)"
                @feedback-close="feedbackDialogMessage = undefined"
                @regenerate="regenerate"
                @auto-retry="regenerate"
                @reset-chat="startNewChat"
                @show-info="emit('show-info', $event)"
                @revise="emit('revise', $event)"
                @upvote="onUpvote"
                @downvote="onDownvote"
                @feedback="onFeedback"
                @approval-respond="addToolApprovalResponse"
                @confirm-respond="onConfirmRespond">
                <template #tool-showContactForm="{ part }">
                    <PkToolShowContactForm
                        :part
                        :readonly="!baseUrl"
                        :submitted="isLeadSubmitted"
                        :loading="isLoadingSubmitLead"
                        :error="submitLeadError"
                        :privacy-policy-notice="
                            agentInterface?.privacyPolicyNotice
                        "
                        @submit="onLeadSubmit" />
                </template>
                <template #tool-showSuggestedReply="{ part }">
                    <PkToolShowSuggestedReply
                        :part
                        @select="onSuggestedReply(part, $event)" />
                </template>
                <template #tool-showSources="{ part }">
                    <PkToolShowSources
                        :part
                        :on-expand-context="handleExpandSourceContext"
                        :on-download="handleDownloadSource" />
                </template>
                <template #tool-showForm="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            :answered="isAnsweredOnCall(part)"
                            @select="onFormAnswer('showForm', part, $event)" />
                    </transition>
                </template>
                <template #tool-showMultipleChoice="{ part }">
                    <transition mode="out-in">
                        <PkToolShowForm
                            :part
                            allow-custom-answer
                            :answered="isAnsweredOnCall(part)"
                            @select="
                                onFormAnswer('showMultipleChoice', part, $event)
                            " />
                    </transition>
                </template>
                <template #tool-requestOAuthConnection="{ part }">
                    <PkToolRequestOAuthConnection
                        :part
                        :resolve-connection="
                            (serverName: string) =>
                                apiClient.getOAuthAuthorizeUrl(
                                    props.agentId,
                                    serverName,
                                )
                        "
                        @connected="onOAuthConnected(part, $event)" />
                </template>
                <template #tool-requestGeolocation="{ part }">
                    <PkToolRequestGeolocation
                        :part
                        :call-live="voice.isActive.value"
                        :reverse-geocode="
                            (lat: number, lon: number) =>
                                apiClient.reverseGeocode(lat, lon)
                        "
                        @result="onGeolocation(part, $event)" />
                </template>
                <template #tool-showLocation="{ part }">
                    <PkToolShowLocation
                        :part
                        :is-dark
                        :main-color="agentInterface?.mainColor"
                        :forward-geocode="
                            (query: string, lang?: string) =>
                                apiClient.forwardGeocode(query, lang)
                        " />
                </template>
                <template #tool-showDiagram="{ part }">
                    <PkToolShowDiagram :part :is-dark />
                </template>
            </PkChatbotMessages>
        </Transition>
        <!-- #endregion -->

        <!-- #region input -->
        <div class="pk-chatbot-view-chat__input-stage">
            <!-- Animated accent glow radiating from the input pill -->
            <Transition name="pk-chatbot-view-chat-fade">
                <PkAuroraCanvas
                    v-if="showEmptyState"
                    class="pk-chatbot-view-chat__aurora"
                    source-selector=".pk-chatbot-input__form" />
            </Transition>
            <div
                v-if="isConversationBlocked"
                class="p-md border-t border-surface-3 text-center text-12 text-danger-darken-2 bg-surface-danger">
                {{ $t('message.chatErrorConversationBlocked') }}
            </div>
            <!-- A live call takes the place of the input: one thing on air, one
             way to hang up, and the conversation stays above it -->
            <PkVoiceSession
                v-else-if="voiceState !== 'idle'"
                :state="voiceState"
                :sample="sampleVoiceLevel"
                :spectrum="sampleVoiceSpectrum"
                :remaining-seconds="voice.remainingSeconds.value"
                :muted="isMuted"
                :error="voice.error.value"
                class="pk-chatbot-view-chat__voice"
                @toggle-mute="toggleVoiceMute"
                @hang-up="stopVoice"
                @retry="retryVoice"
                @dismiss="dismissVoiceError" />
            <PkChatbotInput
                v-else
                v-model="input"
                v-model:pending-attachments="pendingAttachments"
                :placeholder="inputMessagePlaceholder"
                :dismissable-notice="
                    dismissableNotice && chat.messages.length <= 1
                        ? dismissableNotice
                        : undefined
                "
                :status="chat.status"
                :max-message-length="agentInterface?.maxMessageLength"
                :file-upload="agentFileUpload"
                :attachment-limit-reached="isAttachmentLimitReached"
                :attachment-notice="attachmentNotice"
                :voice-enabled="isVoiceEnabled"
                @stop-generation="stopGeneration"
                @submit="storeHandleSubmit"
                @file-select="handleFileSelect"
                @dismiss-attachment-notice="clearFileLimitError"
                @voice-start="startVoice">
                <!-- Fullscreen has no header: the agent identity lives in the
                 input pill -->
                <template v-if="modifier === 'fullscreen'" #prepend>
                    <span class="pk-chatbot-view-chat__agent-chip">
                        <PkAvatar
                            modifiers="surface"
                            class="pk-chatbot-view-chat__agent-chip-avatar"
                            :img-src="agentInterface?.logo"
                            :name="store.name" />
                        <strong
                            v-if="store.name"
                            class="pk-chatbot-view-chat__agent-chip-name">
                            {{ store.name }}
                        </strong>
                    </span>
                </template>
            </PkChatbotInput>
        </div>
        <!-- #endregion -->
        <Transition>
            <div
                v-if="isOverDropZone && agentFileUpload?.enabled"
                class="pk-chatbot-view-chat__drop-overlay">
                <VvIcon
                    name="ri:upload-cloud-2-line"
                    class="pk-chatbot-view-chat__drop-overlay-icon" />
                <span>{{ $t('action.dropFile') }}</span>
            </div>
        </Transition>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-view-chat {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;

        // Empty state (fullscreen): center the greeting + input block
        // vertically in the available space
        &--empty {
            justify-content: center;
            gap: var(--spacing-md);

            // On phones the input docks to the bottom edge instead
            // (Gemini-like), with the greeting centered in the space above
            @include media-breakpoint-down('xs', $breakpoints) {
                justify-content: flex-start;

                .pk-chatbot-empty-state {
                    flex: 1;
                    justify-content: center;
                }
            }
        }

        // Anchors the glow to the input pill
        &__input-stage {
            position: relative;
        }

        // Radiates outwards from the pill, biased upwards (the pill itself
        // paints above, being later in the DOM)
        &__input-stage &__aurora {
            inset: calc(-1 * var(--spacing-224)) calc(-1 * var(--spacing-96))
                calc(-1 * var(--spacing-160));

            // With the input docked at the bottom the glow becomes a wide
            // dome filling the lower part of the screen (Gemini-like)
            @include media-breakpoint-down('xs', $breakpoints) {
                --aurora-spread: 1.6;

                // Tall enough that the glow tail never meets the canvas
                // edge fade (it would draw a visible horizontal band)
                inset: calc(-1 * var(--spacing-384) - var(--spacing-128))
                    calc(-1 * var(--spacing-96)) calc(-1 * var(--spacing-160));
            }
        }

        // Agent identity inside the input pill (fullscreen has no header)
        &__agent-chip {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-6);
            flex-shrink: 0;
            padding: var(--spacing-4) var(--spacing-10) var(--spacing-4)
                var(--spacing-4);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface);

            // Don't crowd the textarea on small screens
            @include media-breakpoint-down('md', $breakpoints) {
                display: none;
            }
        }

        &__agent-chip-avatar {
            width: var(--spacing-24);
            height: var(--spacing-24);
            font-size: var(--text-10);
            border-radius: var(--rounded-full);
        }

        &__agent-chip-name {
            font-size: var(--text-12);
            font-weight: var(--font-semibold);
            color: var(--color-word-2);
            white-space: nowrap;
        }

        // Crossfade empty state ↔ messages: exit faster than enter so the
        // switch never feels like a snap
        &-fade-enter-active {
            transition: opacity 250ms var(--ease-out);
        }

        &-fade-leave-active {
            transition: opacity 150ms var(--ease-out);
        }

        &-fade-enter-from,
        &-fade-leave-to {
            opacity: 0;
        }

        @media (prefers-reduced-motion: reduce) {
            &-fade-enter-active,
            &-fade-leave-active {
                transition: none;
            }
        }

        &__drop-overlay {
            position: absolute;
            inset: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
                var(--spacing-sm);
            z-index: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: var(--spacing-xs);
            background-color: color-mix(
                in srgb,
                var(--color-surface) 85%,
                transparent
            );
            border-radius: var(--rounded-xl);
            border: var(--spacing-2) dashed var(--color-surface-5);
            pointer-events: none;
            color: var(--color-word-3);

            &-icon {
                font-size: var(--spacing-32);
            }
        }
    }
</style>
